import React, { useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Icon from '@mui/material/Icon';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { InputAdornment } from '@mui/material';
import { SubmissionTrackerStatus, VerificationStatus } from '../../../../Component/Model/Enum/BEE123Enums';
import { DataPageDetailDto, UpdateDataPageTrackerDto } from "../../../../Component/Model/Dto/VerificationWorkflow";
import config from "../../../../config";
import {
  VerificationWorkflowService
} from "../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService";
import { toast } from "react-toastify";

interface IProps {
  scorecardId: string;
  hasAccess: boolean;
  dataPage: DataPageDetailDto;
  refresh: () => void;
}
interface ITableData {
  step: string;
  status: SubmissionTrackerStatus | VerificationStatus;
  url: string;
}

const submissionStatusHoverTexts = new Map<SubmissionTrackerStatus, string>([
  [SubmissionTrackerStatus['Planned'], 'Awaiting submission.'],
  [SubmissionTrackerStatus['Submitted, pending VA action'], 'Data page submitted and is awaiting action by your Verification Agency.'],
  [SubmissionTrackerStatus['Submission accepted'], 'Your Verification Agency will proceed to select a sample from your claims, after which, supporting documents for the sampled claims must be uploaded under the "sample selection" tab in the system.'],
  [SubmissionTrackerStatus['Submission rejected'], 'Your Verification Agency will be in contact to discuss next steps.'],
  [SubmissionTrackerStatus['Requested to edit, pending VA action'], 'Data page has been requested to edit and is awaiting action by your Verification Agency.'],
  [SubmissionTrackerStatus['Request to edit accepted - Re-submission'], 'Please update your data as required and submit for verification again.'],
  [SubmissionTrackerStatus['Request to edit accepted - Re-evaluation'], 'Another application link will be emailed to record the re-evaluation. Please complete the application, whereafter your Verification Agency will provide you with access to a new scorecard version on which you can update data page(s).'],
  [SubmissionTrackerStatus['Request to edit rejected'], 'Your Verification Agency will be in contact to discuss next steps.'],
  [SubmissionTrackerStatus['Abandoned'], 'TBC']
]);

const submissionStatusColors = new Map<SubmissionTrackerStatus, string>([
  [SubmissionTrackerStatus['Planned'], '#779DAA'],
  [SubmissionTrackerStatus['Submitted, pending VA action'], '#1C88D1'],
  [SubmissionTrackerStatus['Submission accepted'], '#2E7D32'],
  [SubmissionTrackerStatus['Submission rejected'], '#D32F2F'],
  [SubmissionTrackerStatus['Requested to edit, pending VA action'], '#1C88D1'],
  [SubmissionTrackerStatus['Request to edit accepted - Re-submission'], '#2E7D32'],
  [SubmissionTrackerStatus['Request to edit accepted - Re-evaluation'], '#2E7D32'],
  [SubmissionTrackerStatus['Request to edit rejected'], '#D32F2F'],
  [SubmissionTrackerStatus['Abandoned'], '#779DAA']
]);

const verificationStatusColour = new Map<VerificationStatus, string>([
  [VerificationStatus['Planned'], '#779DAA'],
  [VerificationStatus['In Progress'], '#1C88D1'],
  [VerificationStatus['Completed'], '#2E7D32']
]);

export const StepTable: React.FC<IProps> = (props: IProps) => {

  const [count, setCount] = useState(0);
  const redirectUrl = `${config.apiUrl.replace('/api/', '')}`;
  const [tableData, setTableData] = useState<ITableData[]>([
    {
      step: 'Capture / upload claims',
      status: SubmissionTrackerStatus.Planned,
      url: redirectUrl,
    },
    {
      step: 'Upload general supporting documents',
      status: VerificationStatus.Planned,
      url: redirectUrl,
    },
    {
      step: 'Upload supporting documents as per sample selection',
      status: VerificationStatus.Planned,
      url: redirectUrl,
    },
  ]);

  const getActionButton = (url: string) => {
    return (
      <IconButton onClick={() => props.hasAccess && window.open(url)}>
        <Tooltip title={!props.hasAccess ? "You do not have access to go to this page" : "Go To Page"}>
          <Icon color={!props.hasAccess ? "disabled" : "primary"} fontSize="small">launch</Icon>
        </Tooltip>
      </IconButton>
    );
  };

  const handleVerificationStatusChange = async (value: VerificationStatus, index: number) => {
    const updatedTableData = [...tableData];
    const item = updatedTableData[index];
    item.status = value;
    setTableData(updatedTableData);

    try {
      const data: UpdateDataPageTrackerDto = {
        stepId: index + 1,
        verificationStatus: value,
        dataPageId: props.dataPage.dataPageId,
        scorecardId: props.scorecardId
      }
      await VerificationWorkflowService.UpdateDataPageTrackingProgress(data);
      props.refresh();
    } catch (error) {
      toast.error(`Unable to update ${props.dataPage.name} status`);
    }
    setCount(count + 1);
  };

  useEffect(() => {
    if (props.dataPage) {
      const updatedTableData = [...tableData];
      updatedTableData[0].url += props.dataPage.url;
      updatedTableData[1].url += props.dataPage.url;
      updatedTableData[2].url += props.dataPage.sampleSelectionUrl;

      updatedTableData.forEach((entry, index) => {
        if (index === 0) {
          entry.status = SubmissionTrackerStatus.Planned;
        } else {
          entry.status = VerificationStatus.Planned;
        }
      });

      props.dataPage.trackerInfo.map((item) => {
        const entry = updatedTableData[item.step - 1];
        entry.status = item.step === 1 ? item.submissionTrackerStatus ?? SubmissionTrackerStatus.Planned : item.verificationStatus ?? VerificationStatus.Planned;
      });

      setTableData(updatedTableData);
    }
  }, [props.dataPage]);

  const [openSelects, setOpenSelects] = useState<boolean[]>(new Array(tableData.length).fill(false));

  const toggleSelect = (index: number) => {
    const updatedOpenSelects = [...openSelects];
    updatedOpenSelects[index] = !updatedOpenSelects[index];
    setOpenSelects(updatedOpenSelects);
  };

  return (
    <table style={{ borderCollapse: 'collapse', width: '100%' }} key={`step-table-${count}`}>
      <thead>
        <tr>
          <th style={{ backgroundColor: '#E9ECEF', fontWeight: 'bold', padding: '10px', textAlign: 'left', fontSize: '17px' }}>STEPS TO COMPLETE</th>
          <th style={{ backgroundColor: '#E9ECEF', fontWeight: 'bold', padding: '10px', textAlign: 'left', fontSize: '17px' }}>STATUS</th>
          <th style={{ backgroundColor: '#E9ECEF', fontWeight: 'bold', padding: '10px', textAlign: 'left', display: 'flex', justifyContent: 'center', fontSize: '17px' }}>ACTION</th>
        </tr>
      </thead>
      <tbody>
        {tableData.map((item, index) => {
          const colour = index === 0 ? submissionStatusColors.get(item.status as SubmissionTrackerStatus) : verificationStatusColour.get(item.status as VerificationStatus);
          const hoverText = submissionStatusHoverTexts.get(item.status as SubmissionTrackerStatus);
          return (
            <tr key={`tr-${index}`}>
              <td
                style={{
                  position: 'relative',
                  padding: '10px',
                  borderBottom: '0.5px solid #ccc',
                  fontSize: '15px',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: '0',
                    top: '0',
                    width: '8px',
                    height: '100%',
                    backgroundColor: colour,
                    paddingRight: '5px',
                  }}
                ></div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      backgroundColor: colour,
                      color: '#fff',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginLeft: '5px',
                      marginRight: '5px',
                    }}
                  >
                    {index + 1}
                  </div>
                  {item.step}
                </div>
              </td>
              <td
                style={{
                  backgroundColor: 'white',
                  padding: '10px',
                  borderBottom: '0.5px solid #ccc',
                  fontSize: '15px',
                  width: '500px'
                }}
              >
                <div onClick={() => toggleSelect(index)} style={{ display: 'flex', alignItems: 'center' }}>
                  {index === 0 ? (
                    <Tooltip title={hoverText || ''}>
                      <div
                        style={{
                          position: 'relative',
                          width: '150%',
                          outline: 'none',
                          border: 'none !important',
                          height: '2rem',
                          borderRadius: '2rem',
                          backgroundColor: colour,
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '1rem',
                          paddingRight: '1rem',
                        }}
                      >
                        {SubmissionTrackerStatus[item.status]}
                      </div>
                    </Tooltip>
                  ) : (
                    <Select
                      open={openSelects[index]}
                      onClose={() => toggleSelect(index)} 
                      value={VerificationStatus[item.status]}
                      onChange={(event) => {
                        const value: string = event.target.value;
                        const status: VerificationStatus = VerificationStatus[value as keyof typeof VerificationStatus];
                        handleVerificationStatusChange(status, index)
                      }}
                      style={{
                        height: '2rem',
                        borderRadius: '2rem',
                        color: '#fff',
                        width: '100%'
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton onClick={() => toggleSelect(index)} disabled={!props.hasAccess}>
                            <Icon fontSize="small">keyboard_arrow_down</Icon>
                          </IconButton>
                        </InputAdornment>
                      }
                      disabled={!props.hasAccess}
                    >
                      {Object.values(VerificationStatus)
                        .filter((status) => typeof status === 'string')
                        .map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                </div>
              </td>
              <td
                style={{
                  backgroundColor: 'white',
                  padding: '10px',
                  borderBottom: '0.5px solid #ccc',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {getActionButton(item.url)}
              </td>
            </tr>
          )
        }
        )}
      </tbody>
    </table>
  );
};